import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';

export interface WorkerProfile {
  id: number | null;
  user_id: number;
  persisted?: boolean;
  primary_role: string;
  location: string | null;
  secondary_roles: string[];
  resume_url: string | null;
  headshot_url: string | null;
  demo_reel_url: string | null;
  union_status: string | null;
  iatse_local: string | null;
  certifications: string[];
  rate_min: number | null;
  rate_max: number | null;
  rate_type: string | null;
  equipment_proficiencies: string[];
  work_history: Record<string, unknown>[];
  references_list: Record<string, unknown>[];
  availability: string;
	  travel_willing: boolean;
	  travel_radius_miles: number | null;
	  auto_apply_enabled: boolean;
	  auto_apply_mode: 'automatic' | 'review_only' | 'paused';
	  preferred_job_types: string[];
  excluded_companies: string[];
  min_rate_threshold: number | null;
  weekly_schedule: Record<string, { available: boolean; start: string; end: string }>;
  blocked_dates: Array<{ start: string; end?: string; reason?: string }>;
  buffer_hours: number;
  profile_complete: boolean;
  loyalty_tier: 'new' | 'reliable' | 'pro' | 'elite';
  jobs_completed: number;
  no_show_count: number;
  avg_rating: number | null;
}

export interface WorkerProfileState {
  profile: WorkerProfile | null;
  loading: boolean;
  error: string | null;
}

const initialState: WorkerProfileState = {
  profile: null,
  loading: false,
  error: null,
};

export const fetchProfile = createAsyncThunk(
  'workerProfile/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/worker-profile');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile');
      }
      return rejectWithValue('Failed to fetch profile');
    }
  }
);

export const createProfile = createAsyncThunk(
  'workerProfile/createProfile',
  async (data: Partial<WorkerProfile>, { rejectWithValue }) => {
    try {
      const response = await api.post('/worker-profile', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to create profile');
      }
      return rejectWithValue('Failed to create profile');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'workerProfile/updateProfile',
  async (data: Partial<WorkerProfile> & { userId: number }, { rejectWithValue }) => {
    try {
      const { userId, ...profileData } = data;
      const response = await api.put(`/worker-profile/${userId}`, profileData);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to update profile');
      }
      return rejectWithValue('Failed to update profile');
    }
  }
);

const workerProfileSlice = createSlice({
  name: 'workerProfile',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(createProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default workerProfileSlice.reducer;
